/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import type { PageSpecificAnalyticsDataType } from '@/types/analytics';
import { getAnalyticsRelativeComplementOf } from '@/utils/analytics/getAnalyticsRelativeComplementOf';
import { eventTypes } from '@/constants/event';
import { searchAnalyticsKeys } from '@/constants/analytics/pageSpecificRemoveAnalyticsKeys';
import root from 'window-or-global';
import { getPageName } from '@/utils/analytics/legacyUtils/getPageName';
import { analyticsDynamicPageName } from '@/utils/analytics/legacyUtils/analyticsDynamicPageName';
import { useProductDetails } from '@/features/product/api/getProductDetails';
import { getProductStrikethroughAnalyticValue } from '@/components/ProductDetail/analytics/getProductStrikethroughAnalyticValue';
import { useProductSkuDetails } from '@/features/product/api/getProductSkuDetails';
import { useAnalytics } from '@/utils/analytics/useAnalytics';
import usePrevious from '@/utils/usePrevious';
import { useEffect } from 'react';
import { sessionStorage } from '@/utils/sessionStorage';
import { useFeatureFlag } from '@/features/globalConfig';
import { siteSections } from '@/utils/siteSections';
import { pageTypes } from '@/utils/analytics/legacyUtils/pageTypes';
import { getSeoPageType } from '@/utils/analytics/legacyUtils/getSeoPageType';
import productDetailAnalytics from '@/components/ProductDetail/productDetailAnalytics';
import { useRouter } from 'next/router';
import { useProductSkuDeals } from '@/features/product/api/getProductDeals';
import { type VideoResponseModel, type SylvaniaVideoModel } from '@/api/types/products-types';
import { usePreferredVehicle } from '@/features/header/hooks/usePreferredVehicle';
import { usePageViewCall } from '@/utils/analytics/usePageViewCall';
import { usePageSpecificData } from '@/utils/analytics/usePageSpecificData';
import { useSingleReviewStatistic } from '@/features/customReviews/api/getReviewStatistics';
import { roundValueToPrecision } from '@/components/Rating/utils';
import { keepPreviousData } from '@tanstack/react-query';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';

type AnalyticsViewPayload = {
  categoryLevel1: string;
  categoryLevel2: string;
  categoryLevel3: string;
  categoryLevel4: string;
  pageName: string;
  siteSection: string;
  pageType: string;
  pageHierarchy: string;
  seoPageType?: string;
  seoPopularSearches?: boolean;
  seoPopularSearchesClick: boolean;
  autoCorrectedKeyword: string;
  vehicleId?: string;
  fitsVehicle?: boolean;
  prodView: string;
  skuView: string[];
  productFindingMethod?: string;
  pfmLocation?: string;
  productStrikeThroughPricing: string[];
  searchMethod: string;
  tvVideoName: string;
  videoAvailable: boolean;
  productRank?: string | number | null | undefined;
  vehicleSpecificImage: boolean;
  productAverageRating: number;
  productReviewCount: number;
};

type PdpAnalyticsProps = {
  addingToCartStatus: string;
  sylvaniaRequestIsSuccess: boolean;
  installVideosEnabled: boolean;
  recommendationsClicked: boolean;
  analyticsExecuted: boolean;
  discountedPrice: string | null | undefined;
  vehicleId: string | undefined;
  fitsVehicle: boolean;
  shouldShowDealsButton: boolean | undefined;
  sylvaniaData: VideoResponseModel | undefined;
  partTypeHasVehicleImages: boolean;
  setAnalyticsExecuted: (value: boolean) => void;
};

const { siteSectionPdp, siteSectionCatalog } = siteSections;

const getSylvaniaVideoNames = (videos: SylvaniaVideoModel[] | undefined) => {
  if (!videos) {
    return '';
  }
  const videoNames: string[] = [];
  videos.forEach((video) => {
    video.videos.forEach((singleVideo) => {
      videoNames.push(singleVideo.title);
    });
  });
  return videoNames.join('|');
};

export function usePdpAnalytics({
  recommendationsClicked,
  installVideosEnabled,
  sylvaniaRequestIsSuccess,
  addingToCartStatus,
  analyticsExecuted,
  discountedPrice,
  vehicleId,
  fitsVehicle,
  shouldShowDealsButton,
  sylvaniaData,
  partTypeHasVehicleImages,
  setAnalyticsExecuted,
}: PdpAnalyticsProps) {
  const router = useRouter();

  const { acesEnabled, acesNonVehicleEndpointsEnabled } = useAcesFlags();
  const isNewDealsApiEnabled = useFeatureFlag('IS_NEW_DEALS_API_ENABLED') === 'true';
  const isSuperSkusEnabled = useFeatureFlag('IS_SUPER_SKUS_ENABLED') === 'true';
  const strikeThroughPricingFlag = useFeatureFlag('SHOW_STRIKE_THROUGH_PRICING') === 'true';
  const DLM_PDP_ENABLED = useFeatureFlag('DLM_PDP_ENABLED') === 'true';
  const isCustomReviewsEnabled = useFeatureFlag('CUSTOM_REVIEWS_ENABLED') === 'true';

  const isProductVehicleImageEnabled =
    useFeatureFlag('IS_PRODUCT_VEHICLE_IMAGE_ENABLED') === 'true';

  const isUtagDefined = useAnalytics();

  const { pageSpecificData, setPageSpecificData } = usePageSpecificData();

  const productDetailsQuery = useProductDetails({ placeholderData: keepPreviousData });
  const productDetails = productDetailsQuery.data?.product;
  const productSkuId =
    productDetailsQuery.isSuccess && productDetails?.itemId ? [productDetails?.itemId] : null;
  const skuDetailsResult = useProductSkuDetails({
    skuIds: productSkuId,
  });
  const skuDetailsData = skuDetailsResult.isSuccess ? skuDetailsResult.data[0] : null;
  const prevSkuDetailsData = usePrevious(skuDetailsData);
  const previousPath = usePrevious(router.asPath);

  const { data: skuDealsData } = useProductSkuDeals({
    skuIds: productSkuId,
    enabled: isNewDealsApiEnabled,
  });

  const preferredVehicle = usePreferredVehicle();

  const callPageView = usePageViewCall();

  const isRedirect = Boolean(productDetailsQuery.data?.redirectURL);
  const pdpBreadcrumb = productDetails?.breadCrumbs;

  const vehicleSpecific = productDetails?.vehicleSpecificFlag ?? false;

  const { productFindingMethod: productFindingMethodFromHook, pfmLocation: pfmLocationFromHook } =
    pageSpecificData;

  const { data: reviewStatistic } = useSingleReviewStatistic({
    skuNumber: productDetails?.itemId ?? '',
    enabled: isCustomReviewsEnabled,
  });

  const averageRating = reviewStatistic?.averageOverallRating || '0';
  const rating = roundValueToPrecision(Number(averageRating), 0.1);
  const reviewCount = Number(reviewStatistic?.totalReviewCount || '0');

  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery((theme) => theme.breakpoints.only('md'));

  const screenSize = isMobile ? 'Mobile' : isTablet ? 'Tablet' : 'Desktop';

  useEffect(() => {
    let resultsChangesAfterRecommendationsClicked = false;

    if (recommendationsClicked) {
      resultsChangesAfterRecommendationsClicked = skuDetailsData === prevSkuDetailsData;
    }

    const acceptableReadyStatus =
      productDetailsQuery.isSuccess &&
      skuDetailsResult.isSuccess &&
      !isRedirect &&
      !resultsChangesAfterRecommendationsClicked &&
      (!installVideosEnabled || sylvaniaRequestIsSuccess);

    if (!analyticsExecuted && isUtagDefined && pdpBreadcrumb && acceptableReadyStatus) {
      const pdpUtagViewAffinity = sessionStorage.getItem('pdpUtagViewAffinity') === 'true';
      const productFindingMethodFromUtag =
        typeof root.utag_data?.productFindingMethod === 'string'
          ? root.utag_data?.productFindingMethod
          : '';

      const productFindingMethod =
        sessionStorage.getItem('productFindingMethod') ??
        productFindingMethodFromHook ??
        productFindingMethodFromUtag;

      const pfmLocation =
        productFindingMethod && pfmLocationFromHook
          ? `${productFindingMethod}:${screenSize}:${pfmLocationFromHook}`
          : undefined;

      const isSuperSkuPage =
        isSuperSkusEnabled && !!productDetails?.choices && !!productDetails.availableOptions;
      let dynamicPageName = '';

      if (Object.keys(pdpBreadcrumb).length > 0) {
        const pageName = analyticsDynamicPageName(pdpBreadcrumb, siteSectionPdp);
        const skuId = skuDetailsData?.skuPricingAndAvailability.skuId ?? '';
        dynamicPageName = `${pageName}-${skuId}${isSuperSkuPage ? ':SUPER' : ''}`;
      }

      const breadCrumbList = pdpBreadcrumb.map((crumb) => crumb.displayName);
      const categoryLevel1 = breadCrumbList[1] || '';
      const categoryLevel2 = breadCrumbList[2] || '';
      const categoryLevel3 = breadCrumbList[3] || '';
      const categoryLevel4 = breadCrumbList[4] || '';
      const lwTestExperience = sessionStorage.getItem('lwTestExperience');
      const productStrikeThroughPricing = getProductStrikethroughAnalyticValue(
        skuDetailsData?.skuPricingAndAvailability,
        discountedPrice,
        strikeThroughPricingFlag
      );

      const performedSearch = sessionStorage.getItem('performedSearch') === 'true';
      const productRank = isNaN(Number(sessionStorage.getItem('productRank')))
        ? sessionStorage.getItem('productRank')
        : Number(sessionStorage.getItem('productRank'));

      const analyticsViewPayload: AnalyticsViewPayload = {
        categoryLevel1,
        categoryLevel2,
        categoryLevel3,
        categoryLevel4,
        pageName: getPageName(pageTypes.pdp, dynamicPageName),
        siteSection: siteSectionCatalog,
        pageType: pageTypes.pdp,
        searchMethod: sessionStorage.getItem('searchMethod') || '',
        pageHierarchy: `Home|${
          dynamicPageName.includes('-')
            ? dynamicPageName
                .replace(/-/g, ' ')
                .replace(/:/g, '|')
                .replace(/\b([a-z])/g, (x) => x.toUpperCase())
            : dynamicPageName.replace(/\b([a-z])/g, (x) => x.toUpperCase())
        }`,
        ...(productFindingMethod && { productFindingMethod }),
        ...(pfmLocation && { pfmLocation }),
        ...(lwTestExperience && { lwTestExperience }),
        seoPageType: getSeoPageType(pageTypes.pdp, window?.location.pathname),
        ...(sessionStorage.getItem('productRank') !== null &&
          sessionStorage.getItem('productRank') !== undefined && { productRank }),
        seoPopularSearches: !!document.getElementById('#popular-searches-container'),
        seoPopularSearchesClick: sessionStorage.getItem('popularSearchClicked') === 'true',
        ...productDetailAnalytics({
          productDetails,
          skuDetailsData: skuDetailsData,
          autoCorrectedKeyword: '',
          vehicleId: vehicleId ?? '',
          fitsVehicle,
          vehicleSpecific,
          searchRedirectText:
            typeof router.query.searchText === 'string' ? router.query.searchText : '',
          hasDealBadge: shouldShowDealsButton,
          isNewDealsApiEnabled,
          skuDealsData,
          acesEnabled: acesEnabled && acesNonVehicleEndpointsEnabled,
        }),
        prodView: eventTypes.prodView,
        skuView: ['1'],
        productStrikeThroughPricing: productStrikeThroughPricing,
        tvVideoName: getSylvaniaVideoNames(sylvaniaData?.sylvaniaVideos),
        videoAvailable: installVideosEnabled ? !!sylvaniaData?.sylvaniaVideos?.length : false,
        vehicleSpecificImage:
          isProductVehicleImageEnabled && partTypeHasVehicleImages && !!preferredVehicle,
        productAverageRating: rating,
        productReviewCount: reviewCount,
      };

      if (pdpUtagViewAffinity) {
        const pageSpecificAnalyticsData: PageSpecificAnalyticsDataType = {
          ...analyticsViewPayload,
          productFindingMethod: 'Affinity',
          pfmLocation: 'Affinity:catalog_product_detail',
        };
        callPageView(
          pageSpecificAnalyticsData,
          getAnalyticsRelativeComplementOf(pageSpecificAnalyticsData, undefined)
        );
        sessionStorage.setItem('pdpUtagLinkAffinity', 'true');
        sessionStorage.removeItem('pdpUtagViewAffinity');
      } else {
        const analyticsDataToRemove =
          !performedSearch && typeof router.query.searchText === 'string'
            ? [...searchAnalyticsKeys, 'eventType']
            : searchAnalyticsKeys;

        callPageView(
          analyticsViewPayload,
          getAnalyticsRelativeComplementOf(analyticsViewPayload, analyticsDataToRemove)
        );
      }

      setPageSpecificData({ productFindingMethod: '', pfmLocation: '' });
      sessionStorage.setItem('popularSearchClicked', 'false');
      sessionStorage.removeItem('searchMethod');
      sessionStorage.removeItem('performedSearch');
      sessionStorage.removeItem('lwTestExperience');
      sessionStorage.removeItem('productRank');
      setAnalyticsExecuted(true);
    } else if (analyticsExecuted && previousPath === router.asPath) {
      sessionStorage.removeItem('productFindingMethod');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    acesEnabled,
    acesNonVehicleEndpointsEnabled,
    addingToCartStatus,
    isUtagDefined,
    pdpBreadcrumb,
    skuDetailsResult.isSuccess,
    productDetailsQuery.isSuccess,
    isRedirect,
    analyticsExecuted,
    productDetails,
    skuDetailsData,
    vehicleId,
    fitsVehicle,
    vehicleSpecific,
    router.query.searchText,
    DLM_PDP_ENABLED,
    recommendationsClicked,
    rating,
    reviewCount,
    reviewStatistic,
    isCustomReviewsEnabled,
  ]);
}
